import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Phone } from "lucide-react";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../components/ui/accordion";
import {
  SectionHeading, FeatureCard, StatCard, CheckList, Icon, CTABand,
} from "../components/ui-blocks";
import { home, images, company } from "../mock/mock";

export default function Home() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: company.legal,
    alternateName: "VerifiAssist",
    telephone: company.phone,
    email: company.email,
    description:
      "Remote insurance verification, claims follow-up and revenue cycle support for dental, orthodontic and medical practices.",
    sameAs: [company.socials.facebook, company.socials.instagram, company.socials.linkedin],
  };

  return (
    <>
      <SEO
        title="Insurance Verification & Revenue Cycle Support for Dental & Medical Practices"
        description="VerifiAssist provides remote virtual agents for insurance verification, claims follow-up, prior authorizations and billing — helping dental, orthodontic and medical practices reduce denials and get paid faster without adding payroll."
        path="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-navy">
        <div className="absolute inset-0 grid-dots opacity-30" />
        <div className="absolute -top-24 right-0 h-96 w-96 rounded-full bg-brand-600/30 blur-3xl" />
        <div className="absolute bottom-0 -left-20 h-96 w-96 rounded-full bg-brand-teal/20 blur-3xl" />
        <div className="container-x relative py-16 sm:py-20 lg:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-brand-200">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" />
                {home.hero.eyebrow}
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white text-balance">
                {home.hero.title}
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-brand-100/90">{home.hero.subtitle}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-brand-600 hover:bg-brand-700">
                  <Link to={home.hero.ctaPrimary.to}>{home.hero.ctaPrimary.label} <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">
                  <Link to={home.hero.ctaSecondary.to}>{home.hero.ctaSecondary.label}</Link>
                </Button>
              </div>
              <a href={`tel:${company.phoneRaw}`} className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand-100/80 hover:text-white">
                <Phone className="h-4 w-4 text-brand-teal" /> Call us: {company.phone}
              </a>
            </div>
            <div className="relative animate-fade-up">
              <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
                <img src={images.heroAgent} alt="VerifiAssist remote healthcare agent verifying insurance benefits" className="h-[420px] w-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden sm:block rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600"><Icon name="TrendingUp" className="h-5 w-5" /></div>
                  <div>
                    <div className="text-lg font-bold text-brand-navy">30% fewer denials</div>
                    <div className="text-xs text-slate-500">across verified claims</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {home.hero.stats.map((s) => (
              <StatCard key={s.label} value={s.value} label={s.label} light />
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-slate-200 bg-white">
        <div className="container-x py-6">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Trusted across healthcare & service industries</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {home.trustLogos.map((t) => (
              <span key={t} className="text-sm font-semibold text-slate-500">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="container-x py-16 sm:py-20">
        <SectionHeading
          center
          eyebrow="What we do"
          title="A complete insurance workflow — handled for you"
          subtitle="We plug into your existing systems and standardize the small processes that quietly erode revenue."
        />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {home.valueProps.map((v) => (
            <FeatureCard key={v.title} icon={v.icon} title={v.title} body={v.body} />
          ))}
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-slate-50">
        <div className="container-x py-16 sm:py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="relative">
              <div className="overflow-hidden rounded-3xl shadow-xl">
                <img src={images.femaleDoctor} alt="Confident healthcare professional in a bright clinic" className="h-[440px] w-full object-cover" />
              </div>
            </div>
            <div>
              <SectionHeading eyebrow="Our mission" title={home.mission.title} />
              <div className="mt-5 space-y-4">
                {home.mission.body.map((p, i) => (
                  <p key={i} className="text-slate-600 leading-relaxed">{p}</p>
                ))}
              </div>
              <div className="mt-6">
                <CheckList items={home.mission.bullets} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="container-x py-16 sm:py-20">
        <SectionHeading center eyebrow="Industries we serve" title={home.industries.title} subtitle={home.industries.subtitle} />
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {home.industries.groups.map((g) => (
            <div key={g.title} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-navy text-brand-teal">
                <Icon name={g.icon} className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-brand-navy">{g.title}</h3>
              <ul className="mt-4 space-y-3">
                {g.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Button asChild variant="outline" className="border-brand-200 text-brand-700 hover:bg-brand-50">
            <Link to="/dental-orthodontics">Explore dental & orthodontics support <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-brand-navy">
        <div className="container-x py-16 sm:py-20">
          <SectionHeading center light eyebrow="How it works" title={home.steps.title} subtitle={home.steps.subtitle} />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {home.steps.items.map((s) => (
              <div key={s.n} className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="text-3xl font-bold font-display text-brand-teal">{s.n}</div>
                <h3 className="mt-3 text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-100/80">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-x py-16 sm:py-20">
        <SectionHeading center eyebrow="What practices say" title="Results that show up in your cash flow" />
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {home.testimonials.map((t, i) => (
            <div key={i} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
              <div className="flex gap-1 text-brand-teal">
                {[...Array(5)].map((_, k) => <Star key={k} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="mt-4 flex-1 text-slate-700 leading-relaxed">“{t.quote}”</p>
              <div className="mt-5 border-t border-slate-100 pt-4">
                <div className="text-sm font-semibold text-brand-navy">{t.name}</div>
                <div className="text-xs text-slate-500">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50">
        <div className="container-x py-16 sm:py-20">
          <div className="mx-auto max-w-3xl">
            <SectionHeading center eyebrow="FAQ" title="Questions practices ask us" />
            <Accordion type="single" collapsible className="mt-8">
              {home.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="rounded-xl border border-slate-200 bg-white mb-3 px-5">
                  <AccordionTrigger className="text-left text-brand-navy hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <CTABand
        title="Ready to protect your revenue?"
        body="Insurance breakdowns shouldn't quietly impact your collections. Request your free Insurance Revenue Risk Audit today."
        secondaryTo="/dental-orthodontics"
        secondaryLabel="See dental & ortho savings"
      />
    </>
  );
}
